import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { REACT_APP_WUCA_DXP_ADMIN_URL } from "../../services/config";
import { FileItem, previewFileTypes } from "../../context/model";
import "./ResourcePreviewDialog.scss";
import { getLocalizedValueSync } from "../../services/localizationService";
import { isSafeUrl } from "../../utils/utils";

interface ResourcePreviewDialogProps {
  pageName?: string;
  open: boolean;
  loading: boolean;
  previewFile: FileItem;
  blobUrl: string | null;
  onClose: () => void;
}

const ResourcePreviewDialog: React.FC<ResourcePreviewDialogProps> = ({
  pageName,
  open,
  loading,
  previewFile,
  blobUrl,
  onClose,
}) => {
  const objectRef = useRef<HTMLObjectElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const isIOS = /iPad|iPhone|iPod|Android/i.test(navigator.userAgent)
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  const localizedLoadingFile = getLocalizedValueSync("CA.LoadingFile") || "";
  const localizedPdfNotSupport = getLocalizedValueSync("CA.PdfNotSupport") || "";
  const localizedAudioNotSupport = getLocalizedValueSync("CA.AudioNotSupport") || "";
  const localizedVideoNotSupport = getLocalizedValueSync("CA.VideoNotSupport") || "";
  const localizedPreviewNotSupport = getLocalizedValueSync("CA.PreviewNotSupport") || "";

  const extractFileName = (previewFile: FileItem): string => {
    if (previewFile && previewFile.safeFileName) {
      return previewFile.safeFileName.replace(/[<>:"/\\|?*]+/g, '_');
    } else {
      return 'downloaded-file';
    }

  };
  
  useEffect(() => {
    if (isIOS) {
      const timer = setTimeout(() => {
        if (!isLoaded) {
          // rendering probably failed — auto download
          if (blobUrl && isSafeUrl(blobUrl)){
          const link = document.createElement('a');
          link.href = blobUrl;
          console.log("Downloading file to preview");
          link.download = extractFileName(previewFile);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          onClose();
          } else {
            console.log("Unsafe URL, download aborted");
            onClose();
          }
        }
      }, 1000); // wait 3 seconds before assuming failure

      return () => clearTimeout(timer);
    }
  }, [isLoaded, blobUrl, previewFile]);

  return (
    <Dialog
      open={open}
      className="preview-dialog"
      disableEnforceFocus
      disableEscapeKeyDown
      scroll={"paper"}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-content"
    >
      <DialogTitle id="scroll-dialog-title">{previewFile.fileName}</DialogTitle>
      <button className="preview-dialog-close-btn" onClick={onClose}>
        <span className="fa-kit fa-times"></span>
      </button>
      <DialogContent dividers>
        <div className="preview-dialog-content" id="scroll-dialog-content">
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 65,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <div>{localizedLoadingFile}</div>
            </div>
          )}
          {(() => {
            switch (previewFileTypes[previewFile.fileType || ""]) {
              case "image":
                return (
                  <>
                    {pageName !== 'MyDocuments' &&
                      blobUrl &&
                      <img
                        src={blobUrl || ""}
                        alt={previewFile.fileName}
                        className="preview-dialog-image"
                      />
                    }
                  </>
                );
              case "pdf":
                return (
                  <>

                    <iframe
                      src={blobUrl || ""}
                      title={previewFile.fileName}
                      className="preview-dialog-pdf"
                      frameBorder="0"
                      width="100%"
                      height="100%"
                    >
                      <p>{localizedPdfNotSupport}</p>
                    </iframe>
                  </>
                );
              case "audio":
                return (
                  <>
                    {pageName !== 'MyDocuments' &&
                      blobUrl &&
                      <audio controls className="preview-dialog-audio">
                        <source
                          src={blobUrl || ""}
                          type="audio/mp3"
                        />
                        {localizedAudioNotSupport}
                      </audio>
                    }
                  </>
                );
              case "video":
                return (
                  <>
                    {pageName !== 'MyDocuments' &&
                      blobUrl &&
                      <video controls className="preview-dialog-video"
                      >
                        <source
                          src={blobUrl || ""}
                          type="video/mp4"
                        />
                        {localizedVideoNotSupport}
                      </video>
                    }
                  </>
                );
              default:
                return <p>{localizedPreviewNotSupport}</p>;
            }
          })()}
        </div>
      </DialogContent>
    </Dialog>
  )
};

export default ResourcePreviewDialog;
